import { ColumnState } from 'ag-grid-community';
import { DateRange } from 'react-day-picker';
import { create } from 'zustand';
import { WareHouseDto } from "src/dtos/warehouse/wareHouseDto";
import { defaultWMS1031_R01DetailDto, defaultWMS1031_R01HeaderDto, WMS1031_R01DetailDto, WMS1031_R01HeaderDto } from './Dto_WMS1031_R01';
import { getFutureWeekdayExceptTodayAndTomorrow } from 'src/utils/date/dateUtils';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { showWarning } from 'src/utils/toast/toastUtil';

export interface WMS1031_R01Filter {
	
	dateRange: DateRange | undefined;
	wmsOrderNo: string;
	customCd: string;
	customNm: string;
}
export interface Store_WMS1031_R01 {
	WMS1031_R01Filter: WMS1031_R01Filter;
	WMS1031_R01PastFilter: WMS1031_R01Filter;
	setWMS1031_R01Filter: (filter: Partial<Store_WMS1031_R01['WMS1031_R01Filter']>) => void;
	setWMS1031_R01PastFilter: (filter: Store_WMS1031_R01['WMS1031_R01PastFilter']) => void;
	
	// ====== 매출 리스트 및 선택/편집 ======
	WMS1031_R01List: WMS1031_R01HeaderDto[];
	setWMS1031_R01List: (dataList: WMS1031_R01HeaderDto[]) => void;

	editWMS1031_R01: WMS1031_R01HeaderDto | null;
	setEditWMS1031_R01: (editSale: WMS1031_R01HeaderDto | null) => void;

	selectedWMS1031_R01: WMS1031_R01HeaderDto | undefined;
	setSelectedWMS1031_R01: (data: WMS1031_R01HeaderDto | undefined) => void;
	initSelectedWMS1031_R01: () => void;

	// ====== 상세(디테일) 행 선택/삭제 관리 ======
	deletedWMS1031_R01DetailList: string[];
	setDeletedWMS1031_R01DetailList: (idList: string[]) => void;
	addDeletedWMS1031_R01DetailList: (idList: string[]) => void;
	removeDeletedWMS1031_R01DetailList: (idList?: string[]) => void;

	// ====== 매출 상세(디테일) 데이터 ======
	WMS1031_R01DetailList: WMS1031_R01DetailDto[];
	setWMS1031_R01DetailList: (data: WMS1031_R01DetailDto[]) => void;
	insertWMS1031_R01DetailList: (data: WMS1031_R01DetailDto[], index?: number) => void;
	removeWMS1031_R01DetailList: (index: number) => void;
	updateWMS1031_R01DetailList: (index: number, data: WMS1031_R01DetailDto) => void;

	// ====== 선택(디테일) 행 선택/삭제 관리 ======
	selectedWMS1031_R01DetailList: string[];
	setSelectedWMS1031_R01DetailList: (idList: string[]) => void;
	addSelectedWMS1031_R01DetailList: (idList: string[]) => void;
	removeSelectedWMS1031_R01DetailList: (idList?: string[]) => void;
	initSelectedWMS1031_R01DetailList: () => void;

	originalWMS1031_R01DetailList: WMS1031_R01DetailDto[];
	setOriginalWMS1031_R01DetailList: (data: WMS1031_R01DetailDto[]) => void;

	// ====== 컬럼 상태(그리드) ======
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;
	
	allCount: number;				setAllCount: (allCount: number) => void;

	transportComplete: boolean;		setTransportComplete: (transportComplete: boolean) => void;
	detailFetchTrigger: boolean;	setDetailFetchTrigger: (detailFetchTrigger: boolean) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	// ====== 초기화 ======
	initData: () => void;

	// 창고 코드 필터링 (로직센터 & 자재창고 여부)
	warehouseCdListToUseList: (logicCenter: string) => WareHouseDto[];
	// 공통코드 로드 후 초기값 동기화
	syncInitialData: () => void;
}

export const defaultWMS1031_R01Filter : WMS1031_R01Filter = {
	
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: getFutureWeekdayExceptTodayAndTomorrow()
	},
	wmsOrderNo: '',
	customCd: '',
	customNm: ''
};

const clampUpdateTransportQty = (data: WMS1031_R01DetailDto): WMS1031_R01DetailDto => {
	let qty = Number(data.updateTransportQty);
	if (typeof data.updateTransportQty !== 'number' || isNaN(qty)) {
		showWarning('출고량은 숫자만 입력할 수 있습니다.');
		qty = 0;
	}
	if (qty < 0) {
		showWarning('출고량은 0보다 작을 수 없습니다.');
		qty = 0;
	}
	const stockQty = Number(data.stockQty) || 0;
	if (qty > stockQty) {
		showWarning(`출고량이 재고량(${stockQty})보다 클 수 없습니다. - 품목코드: ${data.itemCd}`);
		qty = stockQty;
	}
	const maxQty = Number(data.orderQtyView) || 0;
	if (qty > maxQty) {
		showWarning(`출고량이 주문잔량(${maxQty})보다 클 수 없습니다. - 품목코드: ${data.itemCd}`);
		qty = maxQty;
	}
	return {
		...data,
		updateTransportQty: qty,
		isModified: qty > 0 ? true : data.isModified,
	};
};

const getInitialWMS1031_R01HeaderDto = (): WMS1031_R01HeaderDto => {
	const { wmsCdList, warehouseCdList } = useCommonCodeStore.getState();
	return {
		...defaultWMS1031_R01HeaderDto,
		logicCenter: wmsCdList?.[0]?.minorCd || '',
		warehouseCd: warehouseCdList?.[0]?.warehouseCd || '',
	};
};

export const useWMS1031_R01Store = create<Store_WMS1031_R01>((set, get) => ({
	WMS1031_R01Filter: defaultWMS1031_R01Filter,
	WMS1031_R01PastFilter: defaultWMS1031_R01Filter,
	setWMS1031_R01Filter: (update) => set((state) => ({
		WMS1031_R01Filter: {
			...state.WMS1031_R01Filter,
			...update
		}
	})),
	setWMS1031_R01PastFilter: (filter: Store_WMS1031_R01['WMS1031_R01PastFilter']) => set((state) => {
		return {
			...state,
			WMS1031_R01PastFilter: filter
		}
	}),

	// ====== 매출 리스트 및 선택/편집 ======
	WMS1031_R01List: [],
	setWMS1031_R01List: (dataList: WMS1031_R01HeaderDto[]) => set((state) => {
		return {
			...state,
			WMS1031_R01List: dataList
		}
	}),

	editWMS1031_R01: getInitialWMS1031_R01HeaderDto(),
	setEditWMS1031_R01: (editSale: WMS1031_R01HeaderDto | null) => set((state) => {
		return {
			...state,
			editWMS1031_R01: editSale
		}
	}),

	selectedWMS1031_R01: undefined,
	
	setSelectedWMS1031_R01: (data: WMS1031_R01HeaderDto | undefined) => set((state) => {
		if (state.selectedWMS1031_R01 === data) return state;

		return { 
			selectedWMS1031_R01: data, 
			selectedWMS1031_R01DetailList: [], deletedWMS1031_R01DetailList: [],
			detailFetchTrigger: true,
			detailScrollTop: null,
		};
	}),
	initSelectedWMS1031_R01: () => set({ 
		selectedWMS1031_R01: undefined, 
		editWMS1031_R01: getInitialWMS1031_R01HeaderDto(), 
		WMS1031_R01DetailList: [],
		detailFetchTrigger: false,
	}),

	// ====== 매출 상세(디테일) 데이터 ======
	WMS1031_R01DetailList: [],
	setWMS1031_R01DetailList: (data: WMS1031_R01DetailDto[]) => set((state) => {
		let newList: WMS1031_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({ 
				...item,
				valueIndex: item.valueIndex > 0 ? item.valueIndex : valueAndOrderIndex,
				orderIndex: item.orderIndex > -1 ? item.orderIndex : valueAndOrderIndex,
				orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			WMS1031_R01DetailList: newList
		}
	}),
	insertWMS1031_R01DetailList: (data: WMS1031_R01DetailDto[], index?: number) => set((state) => {
		let newWMS1031_R01DetailList = [...state.WMS1031_R01DetailList];

		let targetRowData = newWMS1031_R01DetailList.find(item => item.valueIndex === index);
		let targetOrderIndex = targetRowData?.orderIndex || 0;

		const newOrderIndexSeq = (targetRowData?.orderIndexSeq || 0) + 1;
		let maxValueIndex = Math.max(0, ...newWMS1031_R01DetailList.map(item => item.valueIndex || 0)) + 1;

		const targetRows = newWMS1031_R01DetailList.filter(item => item.orderIndex === targetOrderIndex);
		targetRows.forEach(item => {
			if(item.orderIndexSeq < newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq;
			} else if(item.orderIndexSeq >= newOrderIndexSeq) {
				item.orderIndexSeq = item.orderIndexSeq + 1;
			}
		});

		newWMS1031_R01DetailList = newWMS1031_R01DetailList.filter(item => item.orderIndex !== targetOrderIndex);
		newWMS1031_R01DetailList.push(...targetRows);
		
		const newData = data.map(item => ({ ...defaultWMS1031_R01DetailDto, ...item, 
			valueIndex: maxValueIndex++,
			orderIndex: targetOrderIndex,
			orderIndexSeq: newOrderIndexSeq
		}));
		if (index !== undefined) {
			newWMS1031_R01DetailList.splice(index, 0, ...newData);
		} else {
			newWMS1031_R01DetailList.push(...newData);
		}

		return {
			...state,
			WMS1031_R01DetailList: newWMS1031_R01DetailList
		}
	}),
	removeWMS1031_R01DetailList: (index: number) => set((state) => {
		const newWMS1031_R01DetailList = [...state.WMS1031_R01DetailList].filter((item) => item.valueIndex !== index);
		return {
			...state,
			WMS1031_R01DetailList: newWMS1031_R01DetailList
		}
	}),
	updateWMS1031_R01DetailList: (valueIndex: number, data: WMS1031_R01DetailDto) => set((state) => {
		const _WMS1031_R01DetailList = state.WMS1031_R01DetailList.map(item =>
			item.valueIndex === valueIndex ? clampUpdateTransportQty(data) : item
		);
		return {
			...state,
			WMS1031_R01DetailList: _WMS1031_R01DetailList
		}
	}),
	
	// ====== 상세(디테일) 행 선택/삭제 관리 ======
	deletedWMS1031_R01DetailList: [],
	setDeletedWMS1031_R01DetailList: (idList: string[]) => set((state) => {
		return {
			...state,
			deletedWMS1031_R01DetailList: idList
		}
	}),
	addDeletedWMS1031_R01DetailList: (idList: string[]) => set((state) => {
		const _deletedDetailList = [...state.deletedWMS1031_R01DetailList];

		for(const valueIndex of idList) {
			const isInserted = state.WMS1031_R01DetailList.find(item => item.valueIndex === Number(valueIndex) && item.inserted);
			const isModified = state.WMS1031_R01DetailList.find(item => item.valueIndex === Number(valueIndex) && item.isModified);
			if(!isInserted && !isModified) {
				_deletedDetailList.push(valueIndex);
			}
		}
		
		return {
			...state,
			deletedWMS1031_R01DetailList: _deletedDetailList
		}
	}),
	removeDeletedWMS1031_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		deletedWMS1031_R01DetailList: [...state.deletedWMS1031_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),

	selectedWMS1031_R01DetailList: [],
	setSelectedWMS1031_R01DetailList: (idList: string[]) => set((state) => ({
		...state,
		selectedWMS1031_R01DetailList: idList,
	})),
	addSelectedWMS1031_R01DetailList: (idList: string[]) => set((state) => {
		const _selectedWMS1031_R01DetailList = [...state.selectedWMS1031_R01DetailList];
		if(idList.length > 0) {
			_selectedWMS1031_R01DetailList.push(...idList);
		}
		return {
			...state,
			selectedWMS1031_R01DetailList: _selectedWMS1031_R01DetailList
		}
	}),
	removeSelectedWMS1031_R01DetailList: (idList?: string[]) => set((state) => ({
		...state,
		selectedWMS1031_R01DetailList: [...state.selectedWMS1031_R01DetailList].filter(value => idList ? !idList.includes(value) : false)
	})),
	initSelectedWMS1031_R01DetailList: () => set({ selectedWMS1031_R01DetailList: [] }),

	originalWMS1031_R01DetailList: [],
	setOriginalWMS1031_R01DetailList: (data: WMS1031_R01DetailDto[]) => set((state) => {
		let newList: WMS1031_R01DetailDto[] = [];
		let valueAndOrderIndex = 1;
		data.forEach((item) => {
			newList.push({ 
				...defaultWMS1031_R01DetailDto,
				...item,
				valueIndex: valueAndOrderIndex,
				orderIndex: valueAndOrderIndex,
				orderIndexSeq: valueAndOrderIndex
			});
			valueAndOrderIndex++;
		});
		return {
			...state,
			originalWMS1031_R01DetailList: newList
		}
	}),

	// ====== 컬럼 상태(그리드) ======
	columnState: null,
	setColumnState: (state: ColumnState[] | null) => set({ columnState: state }),
	detailColumnState: null,
	setDetailColumnState: (state: ColumnState[] | null) => set({ detailColumnState: state }),

	allCount: 0,					setAllCount: (allCount: number) => set({ allCount }),

	transportComplete: false,		setTransportComplete: (transportComplete: boolean) => set({ transportComplete }),
	detailFetchTrigger: false,		setDetailFetchTrigger: (detailFetchTrigger: boolean) => set({ detailFetchTrigger }),

	scrollTop: null,
	setScrollTop: (top: number | null) => set({ scrollTop: top }),
	detailScrollTop: null,
	setDetailScrollTop: (top: number | null) => set({ detailScrollTop: top }),

	// ====== 초기화 ======
	initData: () => set((state) => ({
		...state,
		WMS1031_R01Filter: defaultWMS1031_R01Filter,
		WMS1031_R01PastFilter: defaultWMS1031_R01Filter,
		WMS1031_R01List: [],
		selectedWMS1031_R01: undefined,
		WMS1031_R01DetailList: [],
		deletedWMS1031_R01DetailList: [],
		selectedWMS1031_R01DetailList: [],
		editWMS1031_R01: getInitialWMS1031_R01HeaderDto(),
		allCount: 0,
		
		transportComplete: false,
		detailFetchTrigger: false,
		scrollTop: null,
		detailScrollTop: null,
	})),

	warehouseCdListToUseList: (logicCenter: string) => {
		const { warehouseCdList } = useCommonCodeStore.getState();
		const filteredList = warehouseCdList.filter(value => (value.logicCenter === logicCenter
			&& (value.goodsYn === 'Y' || value.productYn === 'Y')
		));
		
		if (filteredList.length === 0) {
			return [{ warehouseCd: '', warehouseNm: '' } as WareHouseDto];
		}
		
		return filteredList;
	},

	syncInitialData: () => {
		const { wmsCdList } = useCommonCodeStore.getState();
		const state = get();
		
		if ((state.editWMS1031_R01?.logicCenter && state.editWMS1031_R01?.warehouseCd) || wmsCdList.length === 0) return;

		const defaultLogicCenter = wmsCdList?.[0]?.minorCd || '';
		
		const availableWarehouses = state.warehouseCdListToUseList(defaultLogicCenter);
		const defaultWarehouseCd = availableWarehouses?.[0]?.warehouseCd || '';
		const defaultWarehouseNm = availableWarehouses?.[0]?.warehouseNm || '';

		set((state) => ({
			editWMS1031_R01: {
				...state.editWMS1031_R01!,
				logicCenter: defaultLogicCenter,
				warehouseCd: defaultWarehouseCd,
				warehouseNm: defaultWarehouseNm,
			}
		}));
	},

	
}));
